import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ShieldCheck, Loader2, AlertTriangle, KeyRound } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RECEPCION_LOGIN } from "@/constants/testIds";
import { loginRecepcion } from "@/data/receptionAuthStore";

/**
 * Sprint 47 · Login dedicado del Portal de Recepción.
 * Sólo aceptan las credenciales de RECEPCION/GERENCIA/SISTEMAS.
 */
export default function ReceptionLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  const flash = new URLSearchParams(location.search).get("aviso");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setEnviando(true);
    const res = await loginRecepcion(usuario, password);
    setEnviando(false);
    if (res && !res.error) {
      toast.success(`Sesión iniciada · ${res.nombre}`);
      navigate("/recepcion", { replace: true });
      return;
    }
    if (res?.error === "forbidden") {
      setError(
        res.mensaje ||
          "Este usuario no tiene permiso para operar Recepción."
      );
      return;
    }
    if (res?.error === "credenciales") {
      setError("Usuario o contraseña incorrectos.");
      return;
    }
    if (res?.error === "campos") {
      setError("Ingresa usuario y contraseña.");
      return;
    }
    if (res?.error === "sesion-no-conservada") {
      setError(
        `El servidor aceptó las credenciales, pero este navegador no presentó la sesión al verificarla (HTTP ${res.status || 401} · ${res.categoria || "sin categoría"}). No se concedió acceso.`
      );
      return;
    }
    setError("Sin conexión con el servidor. Intenta de nuevo.");
  };

  return (
    <div
      data-testid={RECEPCION_LOGIN.screen}
      className="min-h-[80vh] flex items-center justify-center px-4 py-10"
    >
      <div className="w-full max-w-md rounded-2xl border hairline bg-card p-8 shadow-soft">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-accent/10 ring-1 ring-accent/30 text-accent grid place-items-center">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.7} />
          </div>
          <div>
            <span className="overline text-accent">CT Connect · Recepción</span>
            <h1 className="font-heading text-2xl leading-tight">
              Acceso al portal
            </h1>
          </div>
        </div>

        {flash === "inactividad" && (
          <div className="mb-4 rounded-lg border hairline bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2 text-xs flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Tu sesión se cerró por inactividad. Vuelve a iniciar sesión.
            </span>
          </div>
        )}
        {flash === "nueva-sesion" && (
          <div className="mb-4 rounded-lg border hairline bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2 text-xs flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Se abrió otra sesión con este usuario. La sesión anterior fue cerrada.
            </span>
          </div>
        )}
        {flash === "sesion-no-reconocida" && (
          <div
            data-testid={RECEPCION_LOGIN.avisoSesion}
            className="mb-4 rounded-lg border hairline bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2 text-xs flex items-start gap-2"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              El servidor no reconoció la sesión en este dispositivo
              {new URLSearchParams(location.search).get("cat")
                ? ` (${new URLSearchParams(location.search).get("cat")})`
                : ""}
              . Inicia sesión de nuevo.
            </span>
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="usuario">Usuario</Label>
            <Input
              id="usuario"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              autoComplete="username"
              autoFocus
              data-testid={RECEPCION_LOGIN.inputUsuario}
              disabled={enviando}
              className="bg-background"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid={RECEPCION_LOGIN.inputPassword}
              disabled={enviando}
              className="bg-background"
            />
          </div>

          {error && (
            <p
              data-testid={RECEPCION_LOGIN.error}
              className="text-xs text-destructive flex items-start gap-1.5"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid={RECEPCION_LOGIN.btnEntrar}
            disabled={enviando}
            className="mt-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {enviando ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Ingresando…
              </>
            ) : (
              <>
                <KeyRound className="w-4 h-4 mr-2" />
                Entrar a Recepción
              </>
            )}
          </Button>

          <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
            Sólo personal autorizado (Recepción · Gerencia · Sistemas). ¿Necesitas otro portal?{" "}
            <Link to="/admin/login" className="underline text-accent">
              Portal admin
            </Link>{" "}
            ·{" "}
            <Link to="/login" className="underline text-accent">
              Portal socio
            </Link>
            .
          </p>
        </form>
      </div>
    </div>
  );
}
